import React, { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import cmtIssueActions from "../../../_actions/_qmsActions/cmtIssueActions";
import CmtListTable, { ChildIssueListContext } from "./CmtListTable";
import "./cmtIssueList.css";
import CmtModal_AddChildIssue from "./CmtModal_AddChildIssue";

export default function CmtChildIssueList(props) {
  const { tabId, divisionId, showAddButton } = props;

  const childIssueListContext = useContext(ChildIssueListContext);

  const dispatch = useDispatch();

  const [modalState_addChild, setModalState_addChild] = useState({
    showModal: false,
    data: null,
  });
  const toggleModal_addChild = () =>
    setModalState_addChild({
      ...modalState_addChild,
      showModal: !modalState_addChild.showModal,
    });

  const issueId = useMemo(
    () => childIssueListContext.data.issueId,
    [childIssueListContext]
  );

  // 'issue' is not passed in thru context. When this component is used as
  // expanded details in CmtList, full issue is not available - only list dto is.
  // 'issue' will only be used if 'fetchListOnly' is false.
  const issue = useSelector((state) =>
    issueId ? state.qms.openIssues[issueId] || null : null
  );

  const qmsTeams = useSelector((state) =>
    divisionId ? state.qms.qmsTeams[divisionId] || null : null
  );
  const cmtCategories = useSelector((state) =>
    divisionId ? state.qms.cmtCategories[divisionId] || null : null
  );

  const childIssueList = childIssueListContext.data.fetchListOnly
    ? useSelector((state) =>
        tabId in state.qms.openIssueLists
          ? "childIssueLists" in state.qms.openIssueLists[tabId]
            ? [issueId] in state.qms.openIssueLists[tabId].childIssueLists
              ? state.qms.openIssueLists[tabId].childIssueLists[issueId]
              : []
            : []
          : []
      )
    : issue.childIssues || [];

  useEffect(() => {
    if (divisionId && issueId && childIssueListContext.data.fetchListOnly)
      dispatch(cmtIssueActions.getChildren(divisionId, issueId, tabId));
  }, [divisionId, issueId]);

  const getStatusColor = useCallback((statusValue) => {
    switch (statusValue) {
      case 0:
      case 1:
        return "badge-light";
      case 2:
        return "badge-warning";
      case 3:
        return "badge-danger";
      default:
        return "badge-light";
    }
  }, []);

  const getOwnerSnippets = useCallback(
    (rowData) => {
      let stageOwners = _.orderBy(
        (qmsTeams || []).filter((qt) =>
          (rowData.currentStageOwnerSnippets || [])
            .filter((sos) => sos.ownerId)
            .map((sos) => sos.ownerId)
            .includes(qt.qmsTeamId)
        ),
        "name",
        "asc"
      );
      if (rowData.primaryOwnerId) {
        const primaryOwner = (qmsTeams || []).find(
          (qt) => qt.qmsTeamId === rowData.primaryOwnerId
        );
        if (primaryOwner)
          stageOwners = [{ ...primaryOwner, isPrimaryOwner: true }, ...stageOwners];
      }
      return stageOwners;
    },
    [qmsTeams]
  );

  // Expand: 0%, Check: 0%, View Button: 10%
  const childColumns = [
    {
      id: "issueNo",
      numeric: false,
      disablePadding: false,
      label: "Child ID",
      width: "10%",
      minWidth: "80px",
      render: (rowData) => (
        <div className="d-flex flex-row flex-wrap issue-list__inline-list">
          <span className="badge badge-light issue-list__inline-list-item">
            {rowData.issueNo}
          </span>
        </div>
      ),
    },
    {
      id: "subject",
      numeric: false,
      disablePadding: false,
      label: "Subject",
      width: "25%",
      minWidth: "200px",
    },
    {
      id: "status",
      numeric: false,
      disablePadding: false,
      label: "Status",
      width: "10%",
      minWidth: "20px",
      render: (rowData) => (
        <div className="d-flex flex-row flex-wrap issue-list__inline-list">
          {rowData.isOpen ? (
            <span className="badge badge-warning issue-list__inline-list-item">Open</span>
          ) : (
            <span className="badge badge-light issue-list__inline-list-item">Closed</span>
          )}
        </div>
      ),
    },
    {
      id: "currentStageTitles",
      numeric: false,
      disablePadding: false,
      label: "Current Stage(s)",
      width: "20%",
      minWidth: "100px",
      render: (rowData) => {
        return (
          <div className="d-flex flex-row flex-wrap issue-list__inline-list">
            {(rowData.currentStageTitles || []).map((st, index) => (
              <span
                key={`currentStageTitleKey_${index}`}
                className="badge badge-secondary issue-list__inline-list-item"
              >
                {st}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      id: "currentStageOwnerSnippets",
      numeric: false,
      disablePadding: false,
      label: "Current Owner(s)",
      width: "20%",
      minWidth: "100px",
      render: (rowData) => {
        const owners = getOwnerSnippets(rowData);
        return (
          <div className="d-flex flex-row flex-wrap issue-list__inline-list">
            {(owners || []).map((o, i) => {
              if (o.isPrimaryOwner) {
                return (
                  <span className="badge badge-primary issue-list__inline-list-item">
                    {`${o.name}`}
                  </span>
                );
              }
              const ownerSnippet = (rowData.currentStageOwnerSnippets || []).find(
                (sos) => sos.ownerId === o.qmsTeamId
              );
              return (
                <span
                  key={`currentStageOwnerKey_${i}`}
                  className={`badge ${getStatusColor(
                    ownerSnippet.status
                  )} issue-list__inline-list-item`}
                >
                  {/* <FontAwesomeIcon
                    icon={getStatusIcon(ownerSnippet.status)}
                    className="fa-fw mr-1"
                  /> */}
                  {`${o.name} (${ownerSnippet.stageCount})`}
                </span>
              );
            })}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="d-flex flex-row flex-wrap w-100">
        <CmtListTable
          divisionId={divisionId}
          data={childIssueList}
          columns={childColumns}
          showAddButton={showAddButton}
          showAddModal={() =>
            setModalState_addChild({
              showModal: true,
              data: { issue },
            })
          }
          isExpandable={false}
          showSelectCheckbox={false}
          filters={[]}
        />
      </div>
      {modalState_addChild.showModal && (
        <CmtModal_AddChildIssue
          modalState={modalState_addChild}
          toggleModal={toggleModal_addChild}
          tabId={tabId}
        />
      )}
    </>
  );
}
